import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchTechnicalAlerts } from '../data/alertsApi';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing technical alerts table data with server-side filtering
 * 
 * @param {Object} filters - Current active filters (filteredInfo object)
 * @param {Object} options - Additional options (pageSize, initialPage, etc.)
 * @returns {Object} - { data, loading, error, pagination, setPage, refetch }
 */
const useTechnicalAlertsTableData = (filters = {}, options = {}) => {
  const { 
    pageSize = 10,
    initialPage = 1,
    filterData = null,
    sorter = null
  } = options;
  
  const { currentUser } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState(null);
  const [allAlertIds, setAllAlertIds] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: initialPage,
    totalPages: 1,
    totalCount: 0,
    pageSize: pageSize
  });

  // Use ref to track the latest filters to avoid stale closures
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Debounce timer ref
  const debounceTimer = useRef(null);

  /**
   * Fetch table data from the API
   */
  const fetchTableData = useCallback(async (page = pagination.currentPage, immediateFilters = null) => {
    const clientId = currentUser?.customer_id;
    
    if (!clientId) {
      setData([]);
      setTotals(null);
      setAllAlertIds([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the provided filters or fall back to current filters
      const currentFilters = immediateFilters || filtersRef.current;
      
      // Map frontend filter keys to backend expected format
      const mappedFilters = {};
      
      Object.entries(currentFilters).forEach(([key, values]) => {
        if (values && Array.isArray(values) && values.length > 0) {
          if (key === 'actions') {
            mappedFilters.status = values;
          } else if (key === 'route') {
            mappedFilters.routes = values;
          } else {
            mappedFilters[key] = values;
          }
        }
      });
      

      // Prepare sort parameters
      const sortParams = {};
      if (sorter && sorter.columnKey && sorter.order) {
        sortParams.column = sorter.columnKey;
        sortParams.order = sorter.order === 'ascend' ? 'asc' : 'desc';
      }

      const result = await fetchTechnicalAlerts(
        clientId,
        mappedFilters, 
        sortParams, 
        page, 
        pagination.pageSize
      );
      
      setData(result.data || []);
      setTotals(result.totals || null);
      
      // Extract all alert IDs from the response  
      // The backend provides allAlertIds which contains ALL IDs across all pages matching current filters
      setAllAlertIds(result.allAlertIds || []);
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: result.pagination.totalPages,
        totalCount: result.pagination.totalCount
      }));
    } catch (err) {
      setError(err);
      // Set empty data on error to allow graceful degradation
      setData([]);
      setTotals(null);
      setAllAlertIds([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalCount: 0
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, sorter, currentUser?.customer_id]);

  /**
   * Debounced fetch function for filter changes
   */
  const debouncedFetch = useCallback((page = 1, immediateFilters = null) => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchTableData(page, immediateFilters);
    }, 300); // 300ms debounce
  }, [fetchTableData]);

  /**
   * Set current page and fetch data
   */
  const setPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchTableData(page);
  }, [fetchTableData]);

  /**
   * Refetch data with current filters and pagination
   */
  const refetch = useCallback((immediateFilters = null) => {
    // Reset to page 1 when filters change
    const targetPage = immediateFilters ? 1 : pagination.currentPage;
    if (immediateFilters) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
    debouncedFetch(targetPage, immediateFilters);
  }, [debouncedFetch, pagination.currentPage]);

  /**
   * Refetch for filter/sort changes - always resets to page 1
   */
  const refetchForFilters = useCallback(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    debouncedFetch(1);
  }, [debouncedFetch]);

  // Effect to handle initial load and filter/sort changes (NOT pagination)
  useEffect(() => {
    // Only fetch when filters/sorter change, not pagination
    refetchForFilters();
  }, [filters, sorter, refetchForFilters]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    totals,
    pagination,
    setPage,
    refetch,
    allAlertIds
  };
};

export default useTechnicalAlertsTableData;
